import { useCallback, useEffect, useState } from 'react';
import { CanceledError } from 'axios';
import { getApiErrorMessage } from '../../helpers/apiError';
import { RECIPIENTS_LOAD_ERROR_MESSAGE, RECIPIENTS_STATUS } from '../../helpers/partnerPreviewRecipients';
import { getPartnerPreviewNotificationRecipients } from '../../services/partnerPreview';

const loadingStateFor = (companyId, isRetrying = false) => {
	return { companyId, status: RECIPIENTS_STATUS.LOADING, recipients: [], error: null, isRetrying };
};

/*
 * Loads who the enrollment email can go to and says what came back: `status` is LOADING, FAILED, EMPTY
 * (a company nobody can be mailed at) or READY, and `isRetrying` is true while a retried request loads.
 * Fetched when the confirmation modal mounts, which is when it opens: the list is transient, per-modal data
 * that no other screen reads, so it stays out of redux. Unmounting aborts the request, and an aborted request
 * never lands: the cleanup is what says whose result is allowed to set state. A response without an `items`
 * array is a failure, not an empty company.
 *
 * Every result remembers the company it belongs to, and a result for another company is read as LOADING while
 * rendering, so no render ever pairs a new `companyId` with the previous company's list (no effect resets it).
 * `retry` resets to LOADING in the handler that asks for it, not in the effect it triggers. Named and default
 * export, like useFleetFirmwareVersions.
 */
export const usePartnerPreviewNotificationRecipients = (companyId) => {
	const [state, setState] = useState(() => {
		return loadingStateFor(companyId);
	});
	const [attempt, setAttempt] = useState(0);

	useEffect(() => {
		const controller = new AbortController();

		const loadRecipients = async () => {
			try {
				const response = await getPartnerPreviewNotificationRecipients(companyId, { signal: controller.signal });

				if (controller.signal.aborted) {
					return;
				}

				const items = response?.data?.items;

				if (!Array.isArray(items)) {
					setState({
						companyId,
						status: RECIPIENTS_STATUS.FAILED,
						recipients: [],
						error: RECIPIENTS_LOAD_ERROR_MESSAGE,
						isRetrying: false
					});
					return;
				}

				setState({
					companyId,
					status: items.length === 0 ? RECIPIENTS_STATUS.EMPTY : RECIPIENTS_STATUS.READY,
					recipients: items,
					error: null,
					isRetrying: false
				});
			} catch (error) {
				if (controller.signal.aborted || error instanceof CanceledError || error?.name === 'AbortError') {
					return;
				}

				setState({
					companyId,
					status: RECIPIENTS_STATUS.FAILED,
					recipients: [],
					error: getApiErrorMessage(error, RECIPIENTS_LOAD_ERROR_MESSAGE),
					isRetrying: false
				});
			}
		};

		loadRecipients();

		return () => {
			controller.abort();
		};
	}, [companyId, attempt]);

	const retry = useCallback(() => {
		setState(loadingStateFor(companyId, true));
		setAttempt((currentAttempt) => {
			return currentAttempt + 1;
		});
	}, [companyId]);

	const { status, recipients, error, isRetrying } = state.companyId === companyId ? state : loadingStateFor(companyId);

	return { status, recipients, error, isRetrying, retry };
};

export default usePartnerPreviewNotificationRecipients;
